<script setup lang="ts">
import { computed, inject } from "vue";

import { labelServiceSymbol } from "@/plugins/LabelServicePlugin";

import { useUnreadNotifications, type UnreadNotificationItem } from "@/composables/unread-notifications";
import { useNotificationsPreference } from "@/composables/notifications-preference";

import type { LabelService } from "@/composables/label-service";

import { formatDateAsMonthDayYearString } from "@tmi/utils/date-time";

const labelService = inject< LabelService >( labelServiceSymbol ) as LabelService;

const {
  acknowledgeItem,
  isAcknowledged,
  markAllAsRead,
  openNotification,
  showNotificationsMenu,
  totalUnreadCount,
  unreadNotifications,
} = useUnreadNotifications();

const { notificationsEnabled, toggle: toggleNotifications } = useNotificationsPreference();

// Badge/list are suppressed when the user has turned notifications off,
// but the bell itself stays visible so the toggle is always reachable.
const displayedUnreadCount = computed< number >( () =>
    notificationsEnabled.value ? totalUnreadCount.value : 0
);

function handleToggleNotifications( event: Event ) {
  event.stopPropagation();
  toggleNotifications();
}

// Close the <details> itself. On @mouseleave the target IS the <details>, but when this runs from
// the dropdown's @click the target is whichever item was tapped, so removing `open` from it did
// nothing and the menu stayed open — on touch there is no mouseleave to fall back on.
function hideMenuOnBlur( event: Event ) {
  const details = ( event.target as Element | null )?.closest( "details" );
  details?.removeAttribute( "open" );
}

function showMenuOnFocus( event: Event ) {
  if ( event.target ) {
    ( event.target as Element ).setAttribute( "open", "" );
  }
}

function handleMarkAllAsRead( event: Event ) {
  event.stopPropagation();
  markAllAsRead();
  hideMenuOnBlur( event as unknown as MouseEvent );
}

function handleOpenNotification( item: UnreadNotificationItem, event: Event ) {
  event.stopPropagation();
  openNotification( item );
  hideMenuOnBlur( event as unknown as MouseEvent );
}

function publishedLabel( item: UnreadNotificationItem ): string {
  return formatDateAsMonthDayYearString( item.content.publishedDate );
}
</script>

<template>
  <details
    v-if="showNotificationsMenu"
    class="header-menu dropdown dropdown-end dropdown-hover"
    @mouseenter="showMenuOnFocus"
    @mouseleave="hideMenuOnBlur"
  >
    <summary class="btn btn-sm btn-ghost relative text-base">
      <i class="pi text-primary lg:inline" :class="notificationsEnabled ? 'pi-bell' : 'pi-bell-slash'" />
      <span class="hidden items-center gap-2 text-base lg:inline">{{ labelService.label( 'lblNotifications' ) }}</span>
      <span
        v-if="displayedUnreadCount > 0"
        class="absolute -top-0.5 -right-0.5 inline-flex min-h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[0.65rem] font-bold leading-none text-white"
        :aria-label="`${ displayedUnreadCount } unread notifications`"
      >
        {{ displayedUnreadCount > 99 ? '99+' : displayedUnreadCount }}
      </span>
    </summary>
    <div
      class="menu dropdown-content z-50 w-[min(100vw-1.5rem,22rem)] rounded-lg border bg-base-100 p-0 shadow-xl dark:border-surface-700 dark:bg-base-200"
      @click="hideMenuOnBlur"
    >
      <div class="flex items-center justify-between gap-2 border-b border-base-300/70 px-3 py-2.5">
        <span class="text-sm font-semibold text-base-content">{{ labelService.label( 'lblNotifications' ) }}</span>
        <div class="flex items-center gap-1">
          <button
            v-if="notificationsEnabled"
            type="button"
            class="btn btn-ghost btn-sm !text-sm font-semibold text-primary"
            :disabled="unreadNotifications.length === 0"
            @click="handleMarkAllAsRead"
          >
            {{ labelService.label( 'lblMarkAllRead' ) }}
          </button>
          <button
            type="button"
            class="btn btn-ghost btn-xs px-1.5"
            :class="notificationsEnabled ? 'text-base-content/60 hover:text-primary' : 'text-primary'"
            :aria-label="labelService.label( notificationsEnabled ? 'lblTurnOffNotifications' : 'lblTurnOnNotifications' )"
            :title="labelService.label( notificationsEnabled ? 'lblTurnOffNotifications' : 'lblTurnOnNotifications' )"
            @click="handleToggleNotifications"
          >
            <i class="pi" :class="notificationsEnabled ? 'pi-bell-slash' : 'pi-bell'" />
          </button>
        </div>
      </div>

      <div v-if="!notificationsEnabled" class="px-4 py-8 text-center text-sm text-base-content/60">
        <i class="pi pi-bell-slash mb-2 text-2xl text-base-content/40" />
        <p class="mb-0 font-medium text-base-content">{{ labelService.label( 'lblNotificationsAreOff' ) }}</p>
        <p class="mb-0 mt-1 text-xs">{{ labelService.label( 'lblNotificationsOffHint' ) }}</p>
        <button
          type="button"
          class="btn btn-primary btn-sm mt-3"
          @click="handleToggleNotifications"
        >
          {{ labelService.label( 'lblTurnOnNotifications' ) }}
        </button>
      </div>

      <div v-else-if="unreadNotifications.length === 0" class="px-4 py-8 text-center text-sm text-base-content/60">
        <i class="pi pi-check-circle mb-2 text-2xl text-primary" />
        <p class="mb-0 font-medium text-base-content">{{ labelService.label( 'lblAllCaughtUp' ) }}</p>
        <p class="mb-0 mt-1 text-xs">{{ labelService.label( 'lblNoUnreadNotifications' ) }}</p>
      </div>

      <ul v-else class="max-h-96 overflow-y-auto p-1">
        <li v-for="item in unreadNotifications" :key="`${ item.contentType }-${ item.content.contentfulId ?? item.content.slug }`">
          <button
            type="button"
            class="flex w-full items-start gap-3 rounded-lg px-2 py-2.5 text-left hover:bg-base-200/80 dark:hover:bg-base-300/40"
            @mouseenter="acknowledgeItem( item )"
            @click="handleOpenNotification( item, $event )"
          >
            <span class="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <i class="pi text-sm" :class="item.icon" />
            </span>
            <span class="min-w-0 flex-1">
              <span class="mb-0.5 block text-[0.65rem] font-bold uppercase tracking-wide text-primary">
                {{ item.contentLabel }}
              </span>
              <span class="line-clamp-2 text-sm font-medium leading-snug text-base-content">
                {{ item.content.title }}
              </span>
              <span class="mt-1 block text-xs text-base-content/55">
                {{ publishedLabel( item ) }}
              </span>
            </span>
            <span
              v-if="!isAcknowledged( item )"
              class="mt-1 size-2 shrink-0 rounded-full bg-red-600"
              aria-hidden="true"
            />
          </button>
        </li>
      </ul>
    </div>
  </details>
</template>
